import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, ValidationErrors } from '@angular/forms';
import { ThemeAssetComponent } from '@partners/themes';
import { FIELD_ERROR_ANIMATION } from '@partners/ui';
import { of } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { IFormField, IValidationHandler, TValidator } from '../../model';
import { FeatureFormComponent } from '../feature-form';

/**
 * @description
 * wrapper for the form field error messages.
 *
 * purpose of this component:
 * 1). it toggles the display of errors containers (only one of them is displays any given moment)
 * 2). it filters error messages to show only first one (todo: add sorting?)
 */

@Component({
  standalone: true,
  selector: 'field-error',
  template: `
    @if(error()) {
    <theme-asset asset="validation-icon"></theme-asset>
    <span class="subscript-text">{{ error() }}</span>
    }
  `,
  animations: [FIELD_ERROR_ANIMATION],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ThemeAssetComponent],
  host: {
    class: 'field-subscript',
    '[class.field-error--shake]': 'withShakeAnimation()'
  },
  exportAs: 'error'
})
export class FieldErrorComponent<T> implements OnInit {
  @Input()
  fieldConfig: IFormField<T>;

  @Input()
  control: FormControl;

  shakeAnimation = input<boolean>(false);

  error = signal<string | undefined>(undefined);
  errorType = signal<TValidator | undefined>(undefined);

  isFormSubmitted = computed<boolean>(() => {
    return this.#parentForm.submitted$$();
  });

  withShakeAnimation = computed<boolean>(() => {
    return this.shakeAnimation() && this.errorType() && this.isFormSubmitted();
  });

  #changeDetector = inject(ChangeDetectorRef);
  #localInjector = inject(Injector);
  #parentForm = inject(FeatureFormComponent);

  ngOnInit(): void {
    this.#addStateListeners();
  }

  #addStateListeners() {
    const { control, validation } = this.fieldConfig;

    const targetControl = this.control || control;

    runInInjectionContext(this.#localInjector, () => {
      toSignal(
        targetControl.statusChanges.pipe(
          startWith(null),
          filter((status: FormControlStatus) => {
            return status !== 'PENDING';
          }),
          map((status) => {
            if (status !== 'VALID') {
              return targetControl?.errors;
            }
            return null;
          }),
          switchMap((rawErrors: ValidationErrors) => {
            const [firstKey] = Object.keys(rawErrors || []);

            if (!firstKey) return of(undefined);

            this.errorType.set(firstKey as TValidator);

            const firstError = { [firstKey]: rawErrors[firstKey] };

            const { validationHandlers } = validation;

            /** find the validator handler which matches the error's name (validator name) */
            const validationHandler: IValidationHandler = validationHandlers.find((v) => {
              if (Array.isArray(v.type)) {
                return v.type.map((t) => t.toLowerCase()).includes(firstKey.toLowerCase());
              }
              return v.type === firstKey;
            });
            this.#changeDetector.markForCheck();

            if (!validationHandler?.buildMessage) {
              return of(undefined);
            }

            /** dynamically build the error message for this error */
            return validationHandler.buildMessage(firstKey as TValidator, firstError);
          }),
          tap((errorText: string | undefined) => {
            this.error.set(errorText);
            // Console.logInfo(`FieldErrorComponent: errorText: ${errorText}`);
            this.#changeDetector.detectChanges();
          })
        )
      );
    });
  }
}
